import {
  Autocomplete,
  Place,
  TUserPlaceType,
  UserPlace,
  createNewImageMarker,
  useGeoveloMap,
  useLayers,
  useSource,
  userPlaceTypes,
} from '@geovelo-frontends/commons';
import { Box, Typography } from '@mui/material';
import { MapMouseEvent, Marker } from 'maplibre-gl';
import { RefObject, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../app/context';
import companyIcon from '../../assets/img/favorite-company.svg';
import usePartnerGeogroupQuery from '../../hooks/use-partner-geogroup-query';
import usePartnerSubgroupsQuery from '../../hooks/use-partner-subgroups-query';

const radiusSourceId = 'radius';
export const placeTypes = { ...userPlaceTypes, company: { color: '#dd428d', icon: companyIcon } };

function PlaceForm({
  mapId,
  initialized: _initialized,
  disableLabel,
  placeType,
  initialPlace,
  updatedPlace,
  isSubmitting,
  markerRef,
  onPlaceUpdated,
  setUpdated,
}: {
  disableLabel?: boolean;
  initialized: boolean;
  initialPlace?: UserPlace | Place | null;
  isSubmitting?: boolean;
  mapId: string;
  markerRef: RefObject<Marker | null>;
  onPlaceUpdated?: (place: Place | null) => void;
  placeType: Extract<TUserPlaceType, 'home' | 'work'> | 'company';
  setUpdated?: (updated: boolean) => void;
  updatedPlace?: UserPlace | Place | null;
}): React.JSX.Element {
  const [initialized, setInitialized] = useState(false);
  const [radiusSourceInitialized, setRadiusSourceInitialized] = useState(false);
  const {
    partner: { current: currentPartner },
  } = useContext(AppContext);
  const { data: geogroup } = usePartnerGeogroupQuery(currentPartner);
  const { data: partnerSubgroups } = usePartnerSubgroupsQuery(currentPartner);
  const sites = partnerSubgroups?.sites;
  const { t } = useTranslation();
  const {
    map,
    init: initMap,
    destroy: destroyMap,
  } = useGeoveloMap({
    smallDevice: true,
    baseLayer: 'geovelo',
  });
  const {
    addGeoJSONSource: addRadiusSource,
    updateGeoJSONSource: updateRadiusSource,
    clearGeoJSONSource: clearRadiusSource,
  } = useSource(map, radiusSourceId);
  const { addCircleLayer } = useLayers(map);

  useEffect(() => {
    if (_initialized) setInitialized(true);
  }, [_initialized]);

  useEffect(() => {
    if (initialized) {
      initMap({
        container: mapId,
        customZoomControls: false,
        disableAttributionControl: true,
        center: initialPlace?.point
          ? { lat: initialPlace.point.coordinates[1], lng: initialPlace.point.coordinates[0] }
          : geogroup?.place
            ? { lat: geogroup.place.point.coordinates[1], lng: geogroup.place.point.coordinates[0] }
            : undefined,
        zoom:
          initialPlace?.point || geogroup?.place ? (placeType === 'company' ? 15 : 13) : undefined,
      });
    }

    return () => {
      markerRef.current = null;
      destroyMap();
    };
  }, [initialized]);

  useEffect(() => {
    if (map) {
      if (placeType !== 'company') {
        addRadiusSource();

        addCircleLayer('radius', radiusSourceId, {
          'circle-radius': [
            'interpolate',
            ['exponential', 2],
            ['zoom'],
            0,
            0,
            20,
            ['get', 'metersToPixelsAtMaxZoom'],
          ],
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.2,
          'circle-stroke-color': ['get', 'color'],
          'circle-stroke-opacity': 0,
        });
      }

      setRadiusSourceInitialized(true);
    }

    return () => {
      if (placeType !== 'company') clearRadiusSource();
      setRadiusSourceInitialized(false);
    };
  }, [map]);

  useEffect(() => {
    function handleClick({ lngLat: { lat, lng } }: MapMouseEvent) {
      map?.flyTo({
        center: { lat, lng },
        zoom: Math.max(placeType === 'company' ? 15 : 13, map.getZoom()),
      });
      onPlaceUpdated?.(new Place(undefined, { type: 'Point', coordinates: [lng, lat] }));
      setUpdated?.(true);
    }

    if (!updatedPlace) map?.on('click', handleClick);

    if (map && radiusSourceInitialized && updatedPlace) {
      const { icon, color } = placeTypes[placeType];
      const { point } = updatedPlace;
      const [lng, lat] = point.coordinates;

      if (placeType !== 'company') {
        const metersToPixelsAtMaxZoom =
          500 / (78271.484 / 2 ** 20) / Math.cos((lat * Math.PI) / 180);

        updateRadiusSource({
          type: 'FeatureCollection',
          features: [
            { type: 'Feature', geometry: point, properties: { color, metersToPixelsAtMaxZoom } },
          ],
        });
      }

      if (markerRef.current) {
        markerRef.current?.setLngLat({ lat, lng });
      } else {
        const marker = createNewImageMarker({
          color,
          icon,
          draggable: true,
          round: true,
          height: 30,
        })
          .setLngLat({ lat, lng })
          .addTo(map);

        marker.on('dragend', ({ target }: { target: Marker }) => {
          map.flyTo({
            center: target.getLngLat(),
            zoom: Math.max(placeType === 'company' ? 15 : 13, map.getZoom()),
          });
          onPlaceUpdated?.(
            new Place(undefined, {
              type: 'Point',
              coordinates: target.getLngLat().toArray(),
            }),
          );
          setUpdated?.(true);
        });

        markerRef.current = marker;
      }
    } else {
      markerRef.current?.remove();
      markerRef.current = null;
      updateRadiusSource({ type: 'FeatureCollection', features: [] });
    }

    return () => {
      map?.off('click', handleClick);
    };
  }, [radiusSourceInitialized, updatedPlace]);

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box display="flex" flexDirection="column" gap={0.5}>
        {!disableLabel && (
          <Typography fontWeight={700} variant="body2">
            {t('companies.pages.admin.user.address_form.labels_address')}
          </Typography>
        )}
        <Autocomplete
          disableFloatingLabel
          customOptions={
            placeType === 'work'
              ? sites && sites.length === 0
                ? geogroup?.place
                  ? [geogroup.place]
                  : []
                : sites?.reduce<Place[]>((res, { place }) => {
                    if (place) res.push(place);
                    return res;
                  }, [])
              : undefined
          }
          defaultValue={updatedPlace || null}
          disabled={isSubmitting}
          label="Ex: 16 rue Saint-Merri 75004 Paris"
          margin="none"
          onSelect={(place) => {
            onPlaceUpdated?.(place);
            setUpdated?.(true);
            if (map && place) {
              const [lng, lat] = place.point.coordinates;
              map.flyTo({
                animate: false,
                center: { lat, lng },
                zoom: Math.max(placeType === 'company' ? 15 : 13, map.getZoom()),
              });
            }
          }}
          size="small"
        />
      </Box>
      <Box display="flex" flexDirection="column" gap={1}>
        <Box height={200} id={mapId} />
        {placeType !== 'company' && (
          <Typography color="textSecondary" variant="caption">
            {t('companies.pages.admin.user.address_form.caption_map_home_work')}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default PlaceForm;
